import React from 'react'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { type Theme, useTheme } from '@mui/material/styles'
import {
  BarPlot,
  type BarSeriesType,
  ChartContainer as ResponsiveChartContainer,
  ChartsReferenceLine,
  ChartsXAxis,
} from '@mui/x-charts'

import { useContainerDimensions } from '../../../insights/hooks/useContainerDimensions'
import type { BarChartV2Props } from '../../../insights/types/Charts/BarChart'
import { LegendV2 } from '../../../insights/components/shared/Charts/BarChart/ChartLegendV2'
import { CustomBarPlotV2 } from '../../../insights/components/shared/Charts/BarChart/CustomBarPlotV2'

export const BarChartV2 = ({
  amounts,
  color,
  data,
  heightOverride,
  margin = {
    bottom: 30,
    left: 16,
    right: 16,
    top: 20,
  },
  onBarClick,
  legendPosition = 'bottom',
  monthlyAmountLabel,
  removeSelectedTabColorChange,
  selectedTabIndex,
  trendAmountLabel,
}: BarChartV2Props) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const { height, width } = useContainerDimensions(containerRef)
  const theme = useTheme()

  const chartData = React.useMemo(() => {
    const values = data.map(({ value }) => value)
    const minValue = Math.min(...values)
    const maxValue = Math.max(...values)

    return {
      series: [
        {
          data: values,
          type: 'bar',
        },
      ] as BarSeriesType[],
      xAxis: [
        {
          data: data.map(({ label }) => label),
          scaleType: 'band' as const,
          categoryGapRatio: 0.45,
        },
      ],
      yAxis: [
        {
          scaleType: 'linear' as const,
          min: minValue < 0 ? minValue * 1.2 : 0,
          max: maxValue > 0 ? maxValue : 0,
        },
      ],
    }
  }, [data])

  const colorRange = React.useMemo(() => {
    const defaultColor = `${color}80`
    if (removeSelectedTabColorChange) return data.map(() => color)

    if (selectedTabIndex == null) {
      return [...new Array(data.length - 1).fill(defaultColor), color]
    }
    return data.map((_, index) => (index === selectedTabIndex ? color : defaultColor))
  }, [color, data, selectedTabIndex])

  const selectedData = React.useMemo(
    () => data[selectedTabIndex ?? data.length - 1],
    [data, selectedTabIndex],
  )

  const lowestValue = data.reduce((min, item) => Math.min(min, item.value), Infinity)
  const isNegative = lowestValue < 0

  const legendComponent = (
    <LegendV2
      color={color}
      legendStyle={{ pl: margin.left }}
      monthlyAmount={selectedData.formattedAmount}
      monthlyAmountLabel={monthlyAmountLabel}
      trendAmount={amounts?.formattedAverage}
      trendAmountLabel={trendAmountLabel}
    />
  )

  const shouldShowLegend = monthlyAmountLabel || trendAmountLabel

  const handleBarClick = (
    _: React.MouseEvent<SVGElement, MouseEvent>,
    { dataIndex }: { dataIndex: number },
  ) => onBarClick?.(dataIndex)

  return (
    <Stack sx={{ alignItems: 'center', width: '100%' }}>
      {shouldShowLegend && legendPosition === 'top' && legendComponent}
      <Box
        ref={containerRef}
        sx={{
          height: heightOverride ? heightOverride : 122,
          mb: legendPosition === 'top' ? 0 : 4,
          mt: legendPosition === 'top' ? 4 : 0,
          touchAction: 'pan-y',
          width: '100%',
          '& svg': {
            touchAction: 'pan-y',
            userSelect: 'none',
          },
        }}
      >
        <ResponsiveChartContainer
          height={height}
          margin={{
            bottom: margin.bottom,
            left: margin.left,
            right: margin.right,
            top: margin.top || 45,
          }}
          series={chartData.series}
          width={width}
          xAxis={chartData.xAxis}
          yAxis={[{ width: 0 }]}
        >
          {amounts?.average && (
            <ChartsReferenceLine
              labelAlign="middle"
              lineStyle={{
                stroke: theme.palette.chart.chart7,
                strokeDasharray: '4',
                strokeLinecap: 'round',
                strokeWidth: 1,
              }}
              y={amounts?.average}
            />
          )}

          {isNegative && (
            <ChartsReferenceLine
              labelAlign="middle"
              lineStyle={{
                stroke: theme.palette.chart.chart7,
              }}
              y={0}
            />
          )}

          <ChartsXAxis
            slots={{
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              axisTickLabel: ({ text, ownerState: _, ...restProps }) => {
                const label = data[selectedTabIndex ?? data.length - 1]?.label
                return (
                  <text
                    {...restProps}
                    style={
                      {
                        ...restProps.style,
                        fontWeight: !removeSelectedTabColorChange && label === text ? 'bold' : 400,
                      } as React.CSSProperties
                    }
                  >
                    {text}
                  </text>
                )
              },
            }}
            sx={(theme) => ({
              '& .MuiChartsAxis-line': {
                stroke: !isNegative ? (theme as Theme).palette.text?.primary : 'none',
              },
              '& .MuiChartsAxis-tickContainer:first-of-type .MuiChartsAxis-tick': {
                display: 'none',
              },
              '& .MuiChartsAxis-tickLabel': {
                transform: isNegative ? 'translateY(15px)' : {},
              },
            })}
            tickLabelStyle={{
              fontSize: 11,
            }}
          />
          <BarPlot
            onItemClick={handleBarClick}
            slots={{
              bar: (props) => {
                const { ownerState, ...defaultProps } = props
                return (
                  <CustomBarPlotV2
                    {...defaultProps}
                    colorRange={colorRange}
                    data={data}
                    dataIndex={ownerState.dataIndex}
                    removeSelectedTabColorChange={removeSelectedTabColorChange}
                    selectedTabIndex={selectedTabIndex ?? data.length - 1}
                    style={{
                      height: props.height,
                      x: props.x,
                      y: props.y,
                      width: props.width,
                    }}
                  />
                )
              },
            }}
          />
        </ResponsiveChartContainer>
      </Box>
      {shouldShowLegend && legendPosition === 'bottom' && legendComponent}
    </Stack>
  )
}
